import {
  appLogout,
  injectSlice,
  createAsyncThunk,
  createAction,
  createSlice,
  PayloadAction,
  createSelector,
} from '@bonanza-social/toolkit/state';
import { AccountService } from '../../api';
import { SourceOfFundsForm } from '../../api/AccountService.schemas';
import {
  Account,
  AccountState,
  UpdateSourceOfFundsRequest,
  UpdateSourceOfFundsThunkConfig,
  MarketingPreferences,
  UpdateEmployerRequestType,
  UpdatePersonalDetailsRequestType,
} from './account.types';

export const updateMFA = createAsyncThunk(
  'account/updateMFA',
  (hasMfaEnabled: boolean) =>
    AccountService.updateMFA({
      data: {
        hasMfaEnabled,
      },
    }),
);

export const updateScreenName = createAction<string>(
  'account/updateScreenName',
);

export const updatePersonalDetails =
  createAction<UpdatePersonalDetailsRequestType>(
    'account/updatePersonalDetails',
  );
export const updateEmployer = createAction<UpdateEmployerRequestType>(
  'account/updateEmployer',
);

export const getAccount = createAsyncThunk('account/get', async () => {
  const account = await AccountService.getAccount({
    params: {
      expand: ['SourceOfFunds', 'PersonalDetails', 'RegistrationMeta'],
    },
  });

  return account;
});

export const requestTimeout = createAsyncThunk(
  'account/requestTimeout',
  async (periodInDays: string) => {
    await AccountService.timeout({
      data: {
        periodInDays,
        isPlayerInitiated: true,
      },
    });
  },
);

export const requestSelfExclusion = createAsyncThunk(
  'account/requestTimeout',
  async ({
    periodInMonths,
    reason,
  }: {
    periodInMonths: string;
    reason?: string;
  }) => {
    await AccountService.selfExclusion({
      data: {
        periodInMonths,
        reason: reason || '',
        isPlayerInitiated: true,
      },
    });
  },
);

export const getMarketingPreferences = createAsyncThunk(
  'marketingPreferences/get',
  async () => {
    const marketingPreferences = await AccountService.getAccount({
      params: {
        expand: 'marketingPreferences',
      },
    });

    return marketingPreferences;
  },
);

export const updateMarketingPreferences = createAsyncThunk(
  'marketingPreferences/update',
  async ({
    allowSMSMarketingTool,
    allowEmailMarketingTool,
    allowInboxMarketingTool,
    allowBigWinNotifications,
  }: MarketingPreferences) => {
    await AccountService.updateMarketingPreferences({
      data: {
        marketingPreferences: {
          allowSMSMarketingTool,
          allowEmailMarketingTool,
          allowInboxMarketingTool,
          allowBigWinNotifications,
        },
      },
    });

    return {
      allowSMSMarketingTool,
      allowEmailMarketingTool,
      allowInboxMarketingTool,
      allowBigWinNotifications,
    };
  },
);

export const updateSourceOfFunds = createAsyncThunk<
  SourceOfFundsForm,
  UpdateSourceOfFundsRequest,
  UpdateSourceOfFundsThunkConfig
>(
  'account/updateSourceOfFunds',
  async ({ id, ...rest }, { rejectWithValue }) => {
    try {
      const data = await AccountService.updateSourceOfFunds({
        pathParams: { id },
        data: rest,
      });
      return data;
    } catch (error) {
      return rejectWithValue(
        error as UpdateSourceOfFundsThunkConfig['rejectValue'],
      );
    }
  },
);

const initialState: AccountState = {
  data: {
    accountId: 0,
    email: '',
    status: 'Pending',
    isTestAccount: false,
    mode: true,
    isMkodoWhitelisted: false,
    hasMFAEnabled: false,
    isPepSettedByPlayer: false,
    liveChatGroupId: 0,
    liveChatCustomerId: '',
    takeABreakEndDate: '',
    personalDetails: undefined,
    verificationMeta: undefined,
    sourceOfFundsForm: undefined,
    rgQuestionnaireForm: undefined,
    marketingPreferences: undefined,
  },
  dataState: 'idle',
  marketingPreferencesDataState: 'idle',
  mfaEnabledDataState: 'idle',
  requestTimeoutDataState: 'idle',
  updateRGQuestionnaireDataState: 'idle',
};

const account = createSlice({
  name: 'account',
  initialState,
  reducers: {
    setAccount(state, action: PayloadAction<Partial<Account>>) {
      if (state.data) {
        state.data = {
          ...state.data,
          ...action.payload,
        };
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAccount.pending, (state) => {
      state.dataState = 'pending';
    });
    builder.addCase(getAccount.fulfilled, (state, action) => {
      state.data = action.payload;
      state.dataState = 'fulfilled';
    });
    builder.addCase(getAccount.rejected, (state) => {
      state.dataState = 'rejected';
    });
    builder.addCase(getMarketingPreferences.fulfilled, (state, action) => {
      if (state.data) {
        state.marketingPreferencesDataState = 'fulfilled';
        state.data.marketingPreferences = action.payload.marketingPreferences;
      }
    });
    builder.addCase(updateMarketingPreferences.rejected, (state) => {
      state.marketingPreferencesDataState = 'rejected';
    });
    builder.addCase(updateMarketingPreferences.pending, (state) => {
      state.marketingPreferencesDataState = 'pending';
    });
    builder.addCase(updateMarketingPreferences.fulfilled, (state, action) => {
      if (state.data) {
        state.data.marketingPreferences = {
          ...state.data.marketingPreferences,
          ...action.payload,
        };
        state.marketingPreferencesDataState = 'fulfilled';
      }
    });
    builder.addCase(updateMFA.pending, (state) => {
      state.mfaEnabledDataState = 'pending';
    });
    builder.addCase(updateMFA.fulfilled, (state, action) => {
      if (state.data) {
        state.data.hasMFAEnabled = action.meta.arg;
      }
      state.mfaEnabledDataState = 'fulfilled';
    });
    builder.addCase(updateMFA.rejected, (state) => {
      state.mfaEnabledDataState = 'rejected';
    });
    builder.addCase(requestTimeout.pending, (state) => {
      state.requestTimeoutDataState = 'pending';
    });
    builder.addCase(requestTimeout.fulfilled, (state) => {
      state.requestTimeoutDataState = 'fulfilled';

      if (state.data) {
        state.data.status = 'Timeout';
      }
    });
    builder.addCase(requestTimeout.rejected, (state) => {
      state.requestTimeoutDataState = 'rejected';
    });

    builder.addCase(appLogout, () => {
      return initialState;
    });
    builder.addCase(updateScreenName, (state, action) => {
      if (state.data?.personalDetails) {
        state.data.personalDetails.screenName = action.payload;
      }
    });
    builder.addCase(updateSourceOfFunds.fulfilled, (state, action) => {
      if (state.data) {
        state.data.sourceOfFundsForm = action.payload;
      }
    });

    builder.addCase(updateSourceOfFunds.rejected, (state, action) => {
      const { errorCode = '' } = action.payload?.data || {};

      if (state.data?.sourceOfFundsForm) {
        if (errorCode === 'account-source-of-funds-form-update-not-allowed') {
          state.data.sourceOfFundsForm.isCompleted = true;
        }

        if (errorCode === 'account-source-of-funds-form-not-found') {
          delete state.data.sourceOfFundsForm;
        }
      }
    });
    builder.addCase(updateEmployer, (state, action) => {
      const { employer: employerPayload } = action.payload;
      if (state.data?.personalDetails?.employer) {
        state.data.personalDetails.employer = {
          ...state.data.personalDetails.employer,
          ...employerPayload,
        };
      }
    });
    builder.addCase(updatePersonalDetails, (state, action) => {
      const { personalDetails: personalDetailsPayload } = action.payload;

      if (state.data?.personalDetails) {
        state.data.personalDetails = {
          ...state.data.personalDetails,
          ...personalDetailsPayload,
        };
      }
    });
  },
});

const withAccount = injectSlice({
  reducerPath: 'account',
  reducer: account.reducer,
});

export const { setAccount } = account.actions;

export const selectAccount = withAccount.selector(
  (state) => state.account.data,
);
export const selectAccountStatus = withAccount.selector(
  (state) => state.account.data?.status,
);

export const isUserOnBreak = withAccount.selector(
  (state) => state.account.data?.status === 'Timeout',
);

export const selectAccountStatusReason = withAccount.selector(
  (state) => state.account.data?.statusReason,
);

export const selectAccountIsWithAMLAssessment = withAccount.selector(
  (state) => state.account.data?.statusReason === 'Within AML Assessment',
);

export const selectAccountEmail = withAccount.selector(
  (state) => state.account.data?.email,
);
export const selectAccountDataState = withAccount.selector(
  (state) => state.account.dataState,
);

export const selectIsPepSettedByPlayer = withAccount.selector(
  (state) => state.account.data?.isPepSettedByPlayer ?? false,
);

export const selectPersonalDetails = withAccount.selector(
  (state) => state.account.data?.personalDetails,
);

export const selectSourceOfFundsForm = withAccount.selector(
  (state) => state.account.data?.sourceOfFundsForm,
);

export const selectRgQuestionnaireForm = withAccount.selector(
  (state) => state.account.data?.rgQuestionnaireForm,
);

export const selectUpdateRGQuestionnaireDataState = withAccount.selector(
  (state) => state.account.updateRGQuestionnaireDataState,
);

export const selectIsSourceOfFundsVisible = withAccount.selector((state) => {
  const form = selectSourceOfFundsForm(state);
  return Boolean(form && !form.isCompleted);
});

export const selectIsTestAccount = withAccount.selector(
  (state) => state.account.data?.isTestAccount,
);

export const selectAccountMFAEnabled = withAccount.selector(
  (state) => state.account.data?.hasMFAEnabled,
);

export const selectAccountMFAEnabledDataState = withAccount.selector(
  (state) => state.account.mfaEnabledDataState,
);

export const selectMarketingPreferences = withAccount.selector(
  (state) => state.account.data?.marketingPreferences,
);

export const selectAccountMarketingMeta = withAccount.selector(
  (state) => state.account.data?.registrationMeta?.marketingMeta,
);

export const selectMarketingPreferencesDataState = withAccount.selector(
  (state) => state.account.marketingPreferencesDataState,
);

export const selectTimeoutEndDate = withAccount.selector(
  (state) => state.account.data?.takeABreakEndDate,
);

export const selectAccountMode = withAccount.selector(
  (state) => state.account.data?.mode,
);

export const selectAccountName = createSelector(
  (state) => state.account.data?.personalDetails,
  (details) => ({
    firstName: details?.firstName,
    middleName: details?.middleName,
    lastName: details?.lastName,
  }),
);

export const selectAccountBirthDay = createSelector(
  (state) => state.account.data?.personalDetails.birthDate,
  (birthDate) => {
    if (!birthDate) return null;

    const date = new Date(birthDate);
    return date.toLocaleDateString('en-CA');
  },
);

export const selectLiveChatGroupId = withAccount.selector((state) => {
  return state.account.data?.liveChatGroupId;
});

export const switchLanguage = createAction<string>('account/switchLanguage');

declare global {
  interface LazyLoadedSlices {
    account: ReturnType<typeof account.reducer>;
  }
}
